import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HeaderButtonComponent } from '../header-button/header-button.component';
import { NavigationService } from '../../../../../services/navigation.service';
import { AuthService } from '../../../../../services/auth.service';
import { AngularModule } from '../../../../../modules/angular.module';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { AdminClientSelectorComponent } from '../admin-client-selector/admin-client-selector.component';
import { ReportingService } from '../../../../../services/reporting.service';
import { LoggedInService } from '../../../../../services/logged-in.service';
import { Subscription } from 'rxjs';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

@Component({
	selector: 'app-page-header',
	standalone: true,
	imports: [HeaderButtonComponent, AngularModule, ZorroModule, AdminClientSelectorComponent, NzTooltipModule],
	templateUrl: './page-header.component.html',
	styleUrl: './page-header.component.scss',
})
export class PageHeaderComponent {
	@Output('onBack') public onBack: EventEmitter<void> = new EventEmitter<void>();
	@Input('useOnBack') public useOnBack: boolean = false;
	@Input('useWindowBack') public useWindowBack: boolean = false;
	@Input('back') public back: string[] = [];
	public isAdmin: boolean = false;
	public isClientAdmin: boolean = false;
	public isDistrictAdmin: boolean = false;
	public isSchoolAdmin: boolean = false;
	public isTeacher: boolean = false;
	public isDemo: boolean = false;
	public isHighestRoleStudent: boolean = false;
	public isClassesPage: boolean = false;
	public isReportsPage: boolean = false;
	public isAdminPage: boolean = false;
	private sub?: Subscription;
	public userDisplayName: string = 'My Account';
	public userHighestRole: string = 'Students';

	constructor(
		public nav: NavigationService,
		public authService: AuthService,
		public reportingService: ReportingService,
		private loggedInService: LoggedInService
	) {
		this.sub = this.nav.routeParts$.subscribe((parts: string[]) => {
			this.isClassesPage = parts.length >= 2 && parts[0] === 'my' && parts[1] === 'sections';
			this.isReportsPage = parts.length >= 2 && parts[0] === 'my' && parts[1] === 'reports';
			this.isAdminPage = parts.length >= 2 && parts[0] === 'my' && parts[1] === 'admin';
		});

		this.sub.add(
			this.authService.user$.subscribe(async user => {
				if (user) {
					this.userDisplayName = user.email || 'My Account';
					this.userHighestRole = await this.authService.getHighestPermission$();
				}
			})
		);

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin = isAdmin;
		});

		this.authService.isClientAdmin$().then((isClientAdmin: boolean) => {
			this.isClientAdmin = isClientAdmin;
		});

		this.authService.isDistrictAdmin$().then((isDistrictAdmin: boolean) => {
			this.isDistrictAdmin = isDistrictAdmin;
		});

		this.authService.isSchoolAdmin$().then((isSchoolAdmin: boolean) => {
			this.isSchoolAdmin = isSchoolAdmin;
		});

		this.authService.isTeacher$().then((isTeacher: boolean) => {
			this.isTeacher = isTeacher;
		});

		this.authService.isDemo$().then((isDemo: boolean) => {
			this.isDemo = isDemo;
		});

		this.authService.isHighestRoleStudent$().then((isHighestRoleStudent: boolean) => {
			this.isHighestRoleStudent = isHighestRoleStudent;
		});
	}

	public backClick(): void {
		if (this.useOnBack) {
			this.onBack.emit();
		} else if (this.useWindowBack) {
			window.history.back();
		} else if (!this.back || this.back.length === 0) {
			this.navigateUpOneLevelNavigation();
		} else {
			this.nav.navigate(this.back);
		}
	}

	public logout(): void {
		this.loggedInService.logout();
	}

	/**
	 * This function will look at the matrix params and determine the navigation
	 * to go 'up one level' in the journey. The 'levels' are as follows:
	 *
	 * 1. Course
	 * 2. Unit
	 * 3. Lesson
	 * 4. Activity
	 *
	 * To go 'up one level' we remove the related uuid from the matrix params.
	 * For example if we have an activityUuid we'd remove that to go up one level. Then
	 * we'd remove the lessonUuid to go up another level.
	 *
	 * @public
	 */
	public navigateUpOneLevelNavigation(): void {
		//Clone the value from the service so we don't modify it
		const matrixParams = { ...this.nav.matrixParams$.value };

		const courseUuid: string | undefined = matrixParams['courseUuid'];
		const locationUuid: string | undefined = matrixParams['locationUuid'];
		const sectionUuid: string | undefined = matrixParams['sectionUuid'];
		const unitUuid: string | undefined = matrixParams['unitUuid'];
		const lessonUuid: string | undefined = matrixParams['lessonUuid'];
		const activityUuid: string | undefined = matrixParams['activityUuid'];

		// If we're at the activity level, remove it to go to lesson+unit level
		if (activityUuid) {
			delete matrixParams['activityUuid'];
		}
		// If we're at the lesson+unit level, remove both to go to course level
		else if (lessonUuid && unitUuid) {
			delete matrixParams['lessonUuid'];
			delete matrixParams['unitUuid'];
		}
		// If we're at the course level, remove everything and go home
		else if (courseUuid && sectionUuid && locationUuid) {
			delete matrixParams['courseUuid'];
			delete matrixParams['sectionUuid'];
			delete matrixParams['locationUuid'];
		}

		// If we have no section UUID, go to home
		if (!matrixParams['sectionUuid']) {
			this.nav.navigate(['/']);
		} else {
			this.nav.navigateWithMatrix$(['/', 'my', 'journey'], matrixParams, false);
		}
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
